import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { clsx } from 'clsx';
import Checkbox from 'components/Checkbox/Checkbox';
import DataSetsItemButtons from 'components/DataSetsItemButtons/DataSetsItemButtons';
import TableCell from 'components/Table/TableCell';
import TableRow from 'components/Table/TableRow';
import TableModal from 'components/TableModal/TableModal';
import { MILLION } from 'constants/dataSets';
import { SELECTED_ITEMS } from 'constants/formFields';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import formattedData from 'utils/formattedData';
import formattedNumberWithComma from 'utils/formattedNumberWithComma';
import getNaceLabel from 'utils/getNaceLabel';

import type { ISuppliersItem } from './interfaces/ISuppliersItem';

import styles from './scss/SuppliersItem.module.scss';

function SuppliersItem({
  item, control, onArchiveModalInfoOpen, onMoveToArchive, onDeArchive, onEditRecord, onDeleteRecord,
  isActionsVisible, naceCodeTreeData, locationsCountries,
}: ISuppliersItem): React.ReactElement {
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const {
    id, name, code, addressBuilding, addressCity, addressCountry, addressPostCode, addressState,
    addressStreet, archived, businessUnitType, dateClosed, dateOpened, naceCode, equityShareholding,
    euTaxonomyCode, financialControl, ownership, uniqueIdentifier, valueChainSection,
    latestArchivation, upstreamTier, naceSection, addressCoordinates, profitCenterLinks,
  } = item;

  const locationCountry = (countryCode: string) => {
    const country = locationsCountries?.find((countryItem) => countryItem.iso2 === countryCode);
    return country?.name || countryCode;
  };

  const { naceCodeLabel, naceSectionLabel } = getNaceLabel(
    { naceCode, naceSection, naceCodeTreeData },
  );

  const { data: userData } = useUserMeQuery();
  const companyCurrency = userData?.data?.company?.currency;

  const hyphen = t('common.hyphen');

  const handleToggleModal = () => {
    setIsModalVisible(!isModalVisible);
  };

  const formattedProfitCenterLinks = profitCenterLinks?.map((link) => ({
    ...link,
    startDate: link.startDate ? new Date(link.startDate).toLocaleDateString() : '',
    endDate: link.endDate ? new Date(link.endDate).toLocaleDateString() : '',
    totalCost: link.totalCost
      ? `${formattedNumberWithComma((Number(link.totalCost.split(' ')[0]) / MILLION).toFixed(2))} M ${companyCurrency}`
      : hyphen,
  }));

  const cellsData = [
    { id: uniqueIdentifier, value: uniqueIdentifier },
    { id: code, value: code },
    { id: valueChainSection, value: valueChainSection },
    { id: upstreamTier, value: upstreamTier },
    { id: ownership, value: ownership },
    { id: businessUnitType, value: businessUnitType },
    {
      id: equityShareholding,
      value: equityShareholding,
      format: (val: number) => `${formattedNumberWithComma(val)} %`,
    },
    {
      id: financialControl,
      value: financialControl,
      format: (val: number) => `${formattedNumberWithComma(val)} %`,
    },
    { id: dateOpened, value: formattedData(dateOpened) },
    { id: dateClosed, value: formattedData(dateClosed) },
    { id: naceSectionLabel, value: naceSectionLabel },
    { id: naceCodeLabel, value: naceCodeLabel },
    { id: euTaxonomyCode, value: euTaxonomyCode },
  ];

  return (
    <>
      <TableRow className={clsx(undefined, { [styles.archived]: archived })}>
        <TableCell>
          <div className={styles.staticCell}>
            {archived ? (
              <span>
                {name}
                {latestArchivation ? (
                  <InfoIcon
                    onClick={() => onArchiveModalInfoOpen(latestArchivation)}
                  />
                ) : null}
              </span>
            ) : null}

            {!archived && (
              isActionsVisible ? (
                <Controller
                  control={control}
                  render={({ field: { onChange, value, disabled } }) => (
                    <Checkbox
                      key={id}
                      name={`${SELECTED_ITEMS}-${id}`}
                      checked={!!value.includes(id)}
                      value={id}
                      disabled={disabled}
                      onChange={(event) => {
                        onChange(event.target.checked
                          ? [...value, id]
                          : value.filter((selectedItem) => selectedItem !== id));
                      }}
                      label={(<span>{name}</span>)}
                    />
                  )}
                  name={SELECTED_ITEMS}
                />
              ) : (<span>{name}</span>)
            )}
          </div>
        </TableCell>
        {cellsData.map((cell, index) => (
          <TableCell key={[cell.id, index].join('_')}>
            {(() => {
              if (!cell.value) return hyphen;
              if (cell.format) return cell.format(cell.value);
              return cell.value;
            })()}
          </TableCell>
        ))}
        <TableCell>
          <div className={styles.profitCenterLinksContainer}>
            <div className={styles.wrapper}>
              {profitCenterLinks.slice(0, 3).map((profitCenterLink) => (
                <div key={profitCenterLink.profitCenterId}>
                  {profitCenterLink.profitCenterId}
                </div>
              ))}
              {profitCenterLinks.length > 3 && (
                <div>...</div>
              )}
            </div>
            <InfoIcon
              onClick={handleToggleModal}
            />
          </div>
        </TableCell>
        <TableCell>
          {locationCountry(addressCountry) || hyphen}
        </TableCell>
        <TableCell>
          {addressState || hyphen}
        </TableCell>
        <TableCell>
          {addressCity || hyphen}
        </TableCell>
        <TableCell>
          {addressPostCode || hyphen}
        </TableCell>
        <TableCell>
          {addressStreet || hyphen}
        </TableCell>
        <TableCell>
          {addressBuilding || hyphen}
        </TableCell>
        <TableCell>
          {addressCoordinates || hyphen}
        </TableCell>

        {isActionsVisible ? (
          <TableCell>
            <DataSetsItemButtons
              id={id}
              archived={archived}
              onEditRecord={onEditRecord}
              onMoveToArchive={onMoveToArchive}
              onDeArchive={onDeArchive}
              onDeleteRecord={onDeleteRecord}
            />
          </TableCell>
        ) : null}
      </TableRow>

      <TableModal
        open={isModalVisible}
        title={t('common.financials', { name })}
        data={formattedProfitCenterLinks}
        onClose={handleToggleModal}
      />
    </>
  );
}

export default SuppliersItem;
